import { useEffect, useState } from "react";
import classNames from "classnames";
import { Spin } from "antd";
import { CheckCircleFilled, DownOutlined } from "@ant-design/icons";
import { AutoSizer, List, type ListRowRenderer } from "react-virtualized";
import type { DsmScopeItem } from "design-scope";
import { ClassBadge } from "./scope-ui";
import "./scope-item.scss";

export type BulkSelectionViewPanelProps = {
  items: DsmScopeItem[];
  className?: string;
  /** Prefix for data-testid attributes (default: scope-bulk-link-dp). */
  "data-testid"?: string;
};

/** Fixed row height so the list can virtualize (py-2 + text-sm + divider). */
const SELECTION_ROW_HEIGHT = 38;
/** Same cap as the old max-h-48 scroll container. */
const SELECTION_LIST_MAX_HEIGHT = 192;

function itemLabel(item: DsmScopeItem): string {
  return item.title?.trim() || item.description?.trim() || "(untitled)";
}

function ClassificationBadges({ item }: { item: DsmScopeItem }) {
  const { uf, mf, label } = item.classification ?? {};
  const hasUf = Boolean(uf?.code || uf?.title);
  const hasMf = Boolean(mf?.code || mf?.title);
  if (!hasUf && !hasMf && label?.title) {
    return <ClassBadge system="CUSTOM" code={label.title} />;
  }
  return (
    <>
      {hasUf && <ClassBadge system="UF" code={uf?.code} />}
      {hasMf && <ClassBadge system="MF" code={mf?.code} />}
    </>
  );
}

/**
 * Grey selection bar with an expandable list of selected scope items
 * (checkmark + title + UF/MF ClassBadges). The list is virtualized so a
 * selection of thousands only draws the rows in view.
 */
export default function BulkSelectionViewPanel({
  items,
  className,
  "data-testid": testId = "scope-bulk-link-dp"
}: BulkSelectionViewPanelProps) {
  const [expanded, setExpanded] = useState(false);
  // Show a loader while the list mounts after "View Selection".
  const [listLoading, setListLoading] = useState(false);
  const selectionKey = items.map((r) => r.id).join(",");

  useEffect(() => {
    setExpanded(false);
  }, [selectionKey]);

  // The click turns the loader on in the same render that expands the panel,
  // so the first paint is the spinner — never the list.
  const toggleExpanded = () => {
    if (expanded) {
      setExpanded(false);
      setListLoading(false);
      return;
    }
    setListLoading(true);
    setExpanded(true);
  };

  useEffect(() => {
    if (!expanded || !listLoading) return undefined;
    // Two frames: the first paints the spinner, the second mounts the rows.
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setListLoading(false));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [expanded, listLoading]);

  if (items.length < 1) return null;

  const listHeight = Math.min(
    items.length * SELECTION_ROW_HEIGHT,
    SELECTION_LIST_MAX_HEIGHT
  );

  const rowRenderer: ListRowRenderer = ({ index, key, style }) => {
    const item = items[index];
    return (
      <div
        key={key}
        style={style}
        className={classNames(
          "box-border flex items-center gap-2 px-3",
          index < items.length - 1 &&
            "border-0 border-b border-solid border-ciq-gray-100"
        )}
        data-testid={`${testId}-selection-row-${item.id}`}
      >
        <CheckCircleFilled className="shrink-0 text-ciq-primary-900" />
        <span className="min-w-0 flex-1 truncate text-sm text-black text-opacity-90">
          {itemLabel(item)}
        </span>
        <span className="flex shrink-0 items-center justify-end gap-1">
          <ClassificationBadges item={item} />
        </span>
      </div>
    );
  };

  return (
    <div
      className={classNames(
        "mb-5 rounded-md bg-ciq-gray-100 px-4 py-2",
        className
      )}
      data-testid={`${testId}-selection`}
    >
      <div className="flex items-center justify-between gap-3">
        <span
          className="rounded-full border border-solid border-ciq-gray-300 bg-white px-3 py-1 text-sm text-ciq-primary-900"
          data-testid={`${testId}-selected-count`}
        >
          {items.length} Selected
        </span>
        <button
          type="button"
          className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-sm text-ciq-primary-900"
          data-testid={`${testId}-view-selection`}
          aria-expanded={expanded}
          onClick={toggleExpanded}
        >
          View Selection
          <DownOutlined
            className={classNames(
              "text-xs transition-transform",
              expanded && "rotate-180"
            )}
          />
        </button>
      </div>

      {expanded && listLoading && (
        <div
          className="mt-2 flex justify-center rounded-md border border-solid border-ciq-gray-300 bg-white py-4"
          data-testid={`${testId}-selection-loading`}
        >
          <Spin size="small" />
        </div>
      )}

      {expanded && !listLoading && (
        <div
          className="mt-2 overflow-hidden rounded-md border border-solid border-ciq-gray-300 bg-white"
          data-testid={`${testId}-selection-list`}
        >
          <AutoSizer disableHeight>
            {({ width }) => (
              <List
                width={width}
                height={listHeight}
                rowCount={items.length}
                rowHeight={SELECTION_ROW_HEIGHT}
                rowRenderer={rowRenderer}
                overscanRowCount={5}
                style={{ outline: "none" }}
              />
            )}
          </AutoSizer>
        </div>
      )}
    </div>
  );
}
